import { Phone, Mail, Clock, MapPin, ShieldCheck, ExternalLink } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { Seo, breadcrumbJsonLd } from "../components/Seo";
import { T, paths, PHONE_HREF, EMAIL_HREF } from "../i18n";
import { CITIES, REGIONS } from "../data/cities";

const COPY = {
  en: {
    phoneH: "24/7 Dispatch Line",
    phoneP: "Call any time — day, night, weekends and holidays. A bilingual supervisor answers, scopes your needs and confirms deployment timelines on the spot.",
    emailH: "Direct Email",
    emailP: "Prefer writing? Send your site address, coverage hours and service needs — we reply with a written quote after a free assessment.",
    hoursH: "Operating Hours",
    hoursP: "Dispatch, patrols and alarm response operate 24 hours a day, 7 days a week, 365 days a year. Office administration: Monday to Friday, 8:00–18:00 ET.",
    coverageH: "Coverage Area",
    coverageP: "We serve 60+ Quebec cities across every administrative region — Montreal, Quebec City, Laval, Gatineau, Longueuil, Sherbrooke, Saguenay, Lévis, Trois-Rivières and beyond.",
    licenseH: "Licensing & Compliance",
    licenseP: "1st Nations Security operates in compliance with Quebec's Private Security Act (Loi sur la sécurité privée). Every guard we deploy holds a valid agent licence issued by the Bureau de la sécurité privée (BSP). We are a 100% First Nations owned company.",
    resourcesH: "Useful Quebec Security Resources"
  },
  fr: {
    phoneH: "Ligne de répartition 24/7",
    phoneP: "Appelez en tout temps — jour, nuit, fins de semaine et jours fériés. Un superviseur bilingue répond, évalue vos besoins et confirme les délais de déploiement sur-le-champ.",
    emailH: "Courriel direct",
    emailP: "Vous préférez écrire? Envoyez l'adresse de votre site, les heures de couverture et vos besoins — nous répondons avec une soumission écrite après une évaluation gratuite.",
    hoursH: "Heures d'ouverture",
    hoursP: "La répartition, les patrouilles et la réponse d'alarme fonctionnent 24 heures sur 24, 7 jours sur 7, 365 jours par année. Administration : lundi au vendredi, 8 h à 18 h (HE).",
    coverageH: "Zone de couverture",
    coverageP: "Nous desservons plus de 60 villes du Québec dans toutes les régions administratives — Montréal, Québec, Laval, Gatineau, Longueuil, Sherbrooke, Saguenay, Lévis, Trois-Rivières et au-delà.",
    licenseH: "Permis et conformité",
    licenseP: "1st Nations Security opère en conformité avec la Loi sur la sécurité privée du Québec. Chaque gardien déployé détient un permis d'agent valide émis par le Bureau de la sécurité privée (BSP). Nous sommes une entreprise détenue à 100 % par les Premières Nations.",
    resourcesH: "Ressources sécurité utiles au Québec"
  }
};

const RESOURCES = [
  ["Bureau de la sécurité privée (BSP)", "https://www.bspquebec.ca", "Quebec's private security licensing authority — verify any agency or guard licence."],
  ["Sûreté du Québec", "https://www.sq.gouv.qc.ca", "Provincial police force serving Quebec's regions and municipalities."],
  ["CNESST", "https://www.cnesst.gouv.qc.ca", "Workplace health and safety standards applicable to security operations."],
  ["Éducaloi — Private Security", "https://educaloi.qc.ca", "Plain-language legal information on private security in Quebec."]
];

export default function ContactPage({ lang }) {
  const t = T[lang];
  const p = paths(lang);
  const c = COPY[lang];

  const title = lang === "fr" ? "Contact — Répartition 24/7 | 1st Nations Security" : "Contact — 24/7 Dispatch | 1st Nations Security";
  const desc = lang === "fr"
    ? "Contactez 1st Nations Security : répartition 24/7 au +1 807-770-0243 ou admin@1stnation-security.com. Compagnie de sécurité 100 % autochtone desservant 60+ villes du Québec."
    : "Contact 1st Nations Security: 24/7 dispatch at +1 807-770-0243 or admin@1stnation-security.com. 100% Indigenous-owned security company serving 60+ Quebec cities.";

  return (
    <div data-testid="contact-page">
      <Seo lang={lang} title={title} description={desc} enPath="/contact" frPath="/fr/contact"
        jsonLd={breadcrumbJsonLd([[t.breadcrumbHome, p.home], [t.nav.contact, p.contact]])} />
      <PageHeader lang={lang} eyebrow={t.brandTag} title={t.contactTitle}
        breadcrumbs={[[t.breadcrumbHome, p.home], [t.nav.contact, null]]} />

      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <Reveal>
          <p className="max-w-2xl text-base text-slate-400">{t.contactSub}</p>
        </Reveal>
        <div className="mt-10 grid gap-6 md:grid-cols-2">
          <Reveal>
            <a href={PHONE_HREF} className="group block h-full rounded-3xl border border-amber-500/30 bg-gradient-to-br from-amber-500/15 to-transparent p-8 transition-all hover:border-amber-400/60 hover:shadow-[0_20px_60px_rgba(245,124,0,0.15)]" data-testid="contact-phone-card">
              <Phone size={30} className="text-amber-400" />
              <h2 className="mt-5 font-display text-2xl font-bold">{c.phoneH}</h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">{c.phoneP}</p>
              <div className="mt-6 font-display text-3xl font-black text-amber-400 group-hover:glow-text">+1 807-770-0243</div>
            </a>
          </Reveal>
          <Reveal delay={0.1}>
            <a href={EMAIL_HREF} className="group block h-full rounded-3xl border border-slate-700 bg-card/70 p-8 transition-all hover:border-amber-500/50" data-testid="contact-email-card">
              <Mail size={30} className="text-amber-400" />
              <h2 className="mt-5 font-display text-2xl font-bold">{c.emailH}</h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">{c.emailP}</p>
              <div className="mt-6 break-all font-display text-xl font-bold text-amber-300">admin@1stnation-security.com</div>
            </a>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-3xl border border-slate-800/70 bg-card/60 p-8" data-testid="contact-hours-card">
              <Clock size={26} className="text-amber-400" />
              <h2 className="mt-4 font-display text-xl font-bold">{c.hoursH}</h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">{c.hoursP}</p>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="rounded-3xl border border-slate-800/70 bg-card/60 p-8" data-testid="contact-coverage-card">
              <MapPin size={26} className="text-amber-400" />
              <h2 className="mt-4 font-display text-xl font-bold">{c.coverageH}</h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">{c.coverageP}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {CITIES.slice(0, 10).map((city) => (
                  <span key={city.slug} className="rounded-full bg-elevated px-3 py-1 text-xs text-slate-400">{lang === "fr" ? city.fr : city.en}</span>
                ))}
                <span className="rounded-full bg-amber-500/15 px-3 py-1 text-xs font-semibold text-amber-300">+{CITIES.length - 10}</span>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal className="mt-10">
          <div className="rounded-3xl border border-amber-500/25 bg-card/70 p-8" data-testid="contact-license-card">
            <ShieldCheck size={26} className="text-amber-400" />
            <h2 className="mt-4 font-display text-xl font-bold">{c.licenseH}</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-400">{c.licenseP}</p>
          </div>
        </Reveal>

        <Reveal className="mt-10">
          <h2 className="font-display text-xl font-bold">{c.resourcesH}</h2>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            {RESOURCES.map(([name, url, d]) => (
              <a key={url} href={url} target="_blank" rel="noopener noreferrer" className="group rounded-2xl border border-slate-800/70 bg-card/60 p-5 transition-all hover:border-amber-500/40" data-testid={`contact-resource-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="flex items-center gap-2 font-semibold text-slate-200 group-hover:text-amber-300">
                  {name} <ExternalLink size={13} className="text-amber-400" />
                </div>
                <p className="mt-1.5 text-xs leading-relaxed text-slate-500">{d}</p>
              </a>
            ))}
          </div>
        </Reveal>
      </section>
    </div>
  );
}
